import React, { useEffect } from 'react';

import { Dialog, useMediaQuery, useTheme } from '@mui/material';
import type { PickerInstance } from 'filestack-js';
import type {
  CreateFileVersionPickerOpts,
  CreateImageUploadFilesPickerOpts,
  CreateOrgAvatarPickerOpts,
  CreateUploadFilesPickerOpts,
} from 'src/helpers';
import {
  useCreateFileVersionPicker,
  useCreateImageUploadFilesPicker,
  useCreateOrgAvatarPicker,
  useCreateUploadFilesPicker,
} from 'src/helpers';
import { useUploadAsLatestVersion } from 'src/helpers/docx-upload-target';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  dialog: {
    // Override MUI v5 Dialog paper styles for Filestack
    '& .MuiDialog-paper': {
      margin: theme.spacing(2),
      maxHeight: `calc(100% - ${theme.spacing(4)})`,
      maxWidth: `calc(100% - ${theme.spacing(4)})`,
      minHeight: 400,
      minWidth: 600,
    },
  },
  fileStackContainer: {
    [theme.breakpoints.between('md', 'lg')]: {
      height: 500,
      width: 600,
    },
    [theme.breakpoints.up('xl')]: {
      height: 600,
      width: 800,
    },
    // Ensure Filestack can inject its content
    '& > *': {
      height: '100%',
      width: '100%',
    },
    // Ensure the inline Filestack picker has space to render
    height: 500,
    minHeight: 400,
    position: 'relative',
    width: 600,
  },
}));

interface UploadFilesPickerProps {
  domContainer: string;
  open: boolean;
  pickerOpts: Omit<CreateUploadFilesPickerOpts, 'confirmUploadAsLatestVersion'>;
  pickerDataCy?: string;
}

interface UploadImageFilesPickerProps {
  domContainer: string;
  open: boolean;
  pickerOpts: CreateImageUploadFilesPickerOpts;
  pickerDataCy?: string;
}

interface UploadFileVersionPickerProps {
  domContainer: string;
  open: boolean;
  pickerOpts: CreateFileVersionPickerOpts;
}

interface UploadOrgAvatarPickerProps {
  domContainer: string;
  open: boolean;
  pickerOpts: CreateOrgAvatarPickerOpts;
}

/**
 * Generic hook to handle FileStack picker lifecycle. Open the picker when the dialog mounts and
 * close it when the dialog unmounts.
 */
function useFilestackPickerLifecycle(
  picker: PickerInstance,
  options: {
    /** Optionally specify one or more initial files to load into the picker when it opens. */
    initialFiles?: Blob[];
  } = {}
) {
  const { initialFiles = [] } = options;

  useEffect(() => {
    if (initialFiles.length) {
      /**
       * This appears to be the only way of prepopulating the file picker with a file.
       * Unfortunately, it also puts the user into cropping mode, so they need to click the Save
       * button before they can click Upload.
       *
       * I've emailed support@filestack.com to ask if any alternative approach is possible. Support
       * ticket 85485. — Joe
       */
      picker.crop(initialFiles);
    } else {
      picker.open();
    }

    return () => {
      picker.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picker, ...initialFiles]);
}

function UploadFilesPickerContent({
  confirmUploadAsLatestVersion,
  domContainer,
  pickerDataCy,
  pickerOpts,
}: {
  confirmUploadAsLatestVersion: CreateUploadFilesPickerOpts['confirmUploadAsLatestVersion'];
  domContainer: string;
  pickerOpts: Omit<CreateUploadFilesPickerOpts, 'confirmUploadAsLatestVersion'>;
  pickerDataCy?: string;
}) {
  const { classes } = useStyles();
  const { picker } = useCreateUploadFilesPicker(domContainer, {
    ...pickerOpts,
    confirmUploadAsLatestVersion,
  });
  useFilestackPickerLifecycle(picker);
  return <div className={classes.fileStackContainer} data-cy={pickerDataCy} id={domContainer} />;
}

function UploadImageFilesPickerContent({
  domContainer,
  pickerDataCy,
  pickerOpts: { initialFiles, ...pickerOpts },
}: {
  domContainer: string;
  pickerOpts: CreateImageUploadFilesPickerOpts;
  pickerDataCy?: string;
}) {
  const { classes } = useStyles();
  const { picker } = useCreateImageUploadFilesPicker(domContainer, pickerOpts);
  useFilestackPickerLifecycle(picker, { initialFiles });
  return <div className={classes.fileStackContainer} data-cy={pickerDataCy} id={domContainer} />;
}

function UploadFileVersionPickerContent({
  domContainer,
  pickerOpts,
}: {
  domContainer: string;
  pickerOpts: CreateFileVersionPickerOpts;
}) {
  const { classes } = useStyles();
  const { picker } = useCreateFileVersionPicker(domContainer, pickerOpts);
  useFilestackPickerLifecycle(picker);
  return <div className={classes.fileStackContainer} id={domContainer} />;
}

function UploadOrgAvatarPickerContent({
  domContainer,
  pickerOpts,
}: {
  domContainer: string;
  pickerOpts: CreateOrgAvatarPickerOpts;
}) {
  const { classes } = useStyles();
  const { picker } = useCreateOrgAvatarPicker(domContainer, pickerOpts);
  useFilestackPickerLifecycle(picker);
  return <div className={classes.fileStackContainer} id={domContainer} />;
}

export function UploadFilesPicker({
  domContainer,
  open,
  pickerDataCy,
  pickerOpts,
}: UploadFilesPickerProps): JSX.Element | null {
  const theme = useTheme();
  const { classes } = useStyles();
  const fullscreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { confirmUploadAsLatestVersion, dialog } = useUploadAsLatestVersion();
  return (
    <>
      <Dialog
        className={classes.dialog}
        fullScreen={fullscreen}
        maxWidth={false}
        open={open}
        onClose={() => {
          pickerOpts.onClose();
        }}
      >
        {open && (
          <UploadFilesPickerContent
            confirmUploadAsLatestVersion={confirmUploadAsLatestVersion}
            domContainer={domContainer}
            pickerDataCy={pickerDataCy}
            pickerOpts={pickerOpts}
          />
        )}
      </Dialog>
      {dialog}
    </>
  );
}

export function UploadImageFilesPicker({
  domContainer,
  open,
  pickerDataCy,
  pickerOpts,
}: UploadImageFilesPickerProps): JSX.Element | null {
  const theme = useTheme();
  const { classes } = useStyles();
  const fullscreen = useMediaQuery(theme.breakpoints.down('sm'));
  return (
    <Dialog
      className={classes.dialog}
      fullScreen={fullscreen}
      maxWidth={false}
      open={open}
      onClose={() => {
        pickerOpts.onClose();
      }}
    >
      {open && (
        <UploadImageFilesPickerContent
          domContainer={domContainer}
          pickerDataCy={pickerDataCy}
          pickerOpts={pickerOpts}
        />
      )}
    </Dialog>
  );
}

export function UploadFileVersionPicker({
  domContainer,
  open,
  pickerOpts,
}: UploadFileVersionPickerProps): JSX.Element | null {
  const theme = useTheme();
  const { classes } = useStyles();
  const fullscreen = useMediaQuery(theme.breakpoints.down('sm'));
  return (
    <Dialog
      className={classes.dialog}
      fullScreen={fullscreen}
      maxWidth={false}
      open={open}
      onClose={() => {
        pickerOpts.onClose();
      }}
    >
      {open && (
        <UploadFileVersionPickerContent domContainer={domContainer} pickerOpts={pickerOpts} />
      )}
    </Dialog>
  );
}

export function UploadOrgAvatarPicker({
  domContainer,
  open,
  pickerOpts,
}: UploadOrgAvatarPickerProps): JSX.Element | null {
  const theme = useTheme();
  const { classes } = useStyles();
  const fullscreen = useMediaQuery(theme.breakpoints.down('sm'));
  return (
    <Dialog
      className={classes.dialog}
      fullScreen={fullscreen}
      maxWidth={false}
      open={open}
      onClose={() => {
        pickerOpts.onClose();
      }}
    >
      {open && <UploadOrgAvatarPickerContent domContainer={domContainer} pickerOpts={pickerOpts} />}
    </Dialog>
  );
}
